<page-title [value]="'Profile' | translate" />
<h3 class="page-title simple no-wrap">
  <span>{{ "Profile" | translate }}: {{ user.displayName }}</span>
</h3>
<ul class="nav-pills align-items-baseline" ngbNav #nav="ngbNav">
  <li [ngbNavItem]="ProfileViewTabEnum.profile" data-test-qa-tab="profile">
    <a ngbNavLink translate>Profile</a>
    <div class="content-box force-validate" *ngbNavContent ngForm #form="ngForm">
      <div class="d-flex">
        <div class="me-5 mt-3">
          <div class="avatar-box">
            <avatar [user]="$any(user)" [avatarVersion]="avatarVersion" size="xl" />
            <div class="btn-group">
              <button (click)="changeAvatar()" data-test-qa="changeAvatar">
                <i class="far fa-pencil"></i>
              </button>
              @if (user.avatar) {
                <button (click)="removeAvatar()" data-test-qa="removeAvatar">
                  <i class="far fa-trash-alt"></i>
                </button>
              }
            </div>
          </div>
        </div>
        <div class="ms-3 w-100">
          <div class="form-line">
            <div class="header">
              <div class="title" translate>Full name</div>
              <div class="description" translate>
                Display name used throughout the web interface
              </div>
            </div>
            <input
              class="form-control"
              type="text"
              name="profileView-fullName"
              [(ngModel)]="user.fullName"
              autocapitalize="off"
              autocorrect="off"
              maxlength="255"
              id="profileView-fullName"
              [srLabel]="'Full name' | translate"
            />
          </div>
          <div class="form-line">
            <div class="header">
              <div class="title" translate>Username</div>
            </div>
            <div class="form-control-static selectable">{{ user.username }}</div>
          </div>
          <div class="form-line">
            <div class="header">
              <div class="title" translate>UNIX name</div>
              <div class="description" translate>
                Used to connect to workspaces and shares using the OS tools
              </div>
            </div>
            <div class="form-control-static selectable" data-test-qa="profileView-unixName">
              {{ user.unixUsername || ("Not available yet" | translate) }}
            </div>
          </div>
          <div class="form-line">
            <div class="header">
              <div class="title" translate>Display language</div>
            </div>
            <select class="form-control" name="userLanguage" [(ngModel)]="user.language">
              <option [ngValue]="null">
                {{ "Default" | translate }} ({{ parameters.values.language }})
              </option>
              @for (lang of languages; track lang) {
                <option [ngValue]="lang.code">
                  {{ lang.name | translate }}
                </option>
              }
            </select>
          </div>
          @if (!user.ldap && user.allowChangingPassword) {
            <div class="form-line">
              <div class="header">
                <div class="title" translate>Password</div>
              </div>
              <button
                class="btn btn-secondary"
                (click)="changePassword()"
                [class]="{ 'btn-secondary': user.hasPassword, 'btn-info': !user.hasPassword }"
                data-test-qa="changePassword"
              >
                <i class="far fa-pencil"></i>
                @if (!user.hasPassword) {
                  <span translate>Set password</span>
                }
                @if (user.hasPassword) {
                  <span translate>Change password</span>
                }
              </button>
            </div>
          }
          <div class="form-line">
            <div class="header">
              <div class="title" translate>Two-factor authentication</div>
              <div class="description" translate>
                Require a one-time code in addition to the password
              </div>
            </div>
            <user-totp-setup-button [user]="$any(user)" [disabled]="!totpChangesAllowed" />
          </div>
          <div class="form-line">
            <div class="header">
              <div class="title" translate>Default page</div>
            </div>
            <better-select
              name="profileView-defaultPage"
              [(ngModel)]="user.defaultPage"
              [items]="availablePages"
              [filterKeys]="['name']"
              valueField="id"
            >
              <ng-template let-item>
                <div class="d-flex align-items-center">
                  <i class="far fa-fw" [class]="'fa-' + item.icon"></i>
                  <div class="ms-2">{{ identity.getPageFullName(item) | translate }}</div>
                </div>
              </ng-template>
            </better-select>
          </div>
        </div>
      </div>
      <floating-toolbar>
        <save-button [click]="save" [disabled]="!form.form.valid" />
      </floating-toolbar>
    </div>
  </li>
  @if (parameters.values.userNotificationSettings) {
    <li [ngbNavItem]="ProfileViewTabEnum.notifications" data-test-qa-tab="notifications">
      <a ngbNavLink translate>Notifications</a>
      <div *ngbNavContent>
        <notification-settings [user]="$any(user)" />
      </div>
    </li>
  }
  <li [ngbNavItem]="ProfileViewTabEnum.api" data-test-qa-tab="api">
    <a ngbNavLink translate>API</a>
    <div *ngbNavContent>
      <div class="form-line">
        <div class="header">
          <div class="title" translate>API Documentation</div>
        </div>
        <a
          class="btn btn-secondary ms-3"
          routerLink="/profile/api-playground"
          data-test-qa="apiPlaygroundButton"
        >
          <i class="far fa-flask"></i>
          <span translate>API Playground</span>
        </a>
        <a class="btn btn-secondary ms-3" href="https://python.sdk.elements.tv/" target="_blank">
          <i class="far fa-book"></i>
          <span translate>SDK Docs</span>
          <i class="far fa-external-link ms-2"></i>
        </a>
      </div>
      <div class="form-line">
        <div class="header">
          <div class="title" translate>API Tokens</div>
          <div class="description" translate>
            Tokens allow making API calls on behalf of your account
          </div>
        </div>
        <div>
          <button class="btn btn-secondary" (click)="createToken()" data-test-qa="createToken">
            <i class="far fa-plus"></i>
            <span translate>Create token</span>
          </button>
        </div>
      </div>
      <entity-list [entities]="tokens" className="list-group list-group-light">
        <div class="list-group-item d-flex" *entityListItem="let token">
          <i class="far fa-key me-2"></i>
          <div class="me-auto">{{ token.name }}</div>
          @if (token.token) {
            <div class="input-group ms-5" data-test-qa="tokenSecret">
              <div class="input-group-prepend">
                <div class="input-group-text">{{ "Token" | translate }}:</div>
              </div>
              <input class="form-control" type="text" readonly [value]="token.token" autoselect />
              <div class="input-group-append">
                <button class="btn btn-info" [copyToClipboardButton]="token.token">
                  <i class="far fa-fw fa-copy"></i>
                </button>
              </div>
            </div>
          }
          @if (!token.token) {
            <button
              class="btn btn-secondary hover-reveal"
              (click)="deleteToken(token)"
              data-test-qa="deleteToken"
            >
              <i class="fa fa-trash-alt"></i>
              <span class="visually-hidden" translate>Revoke</span>
            </button>
          }
        </div>
      </entity-list>
    </div>
  </li>

  @if (parameters.values.mcpEnabled) {
    <li [ngbNavItem]="ProfileViewTabEnum.agents" data-test-qa-tab="agents">
      <a ngbNavLink translate>
        AI Agents
        <badge class="ms-2" scheme="rare">
          <badge-prefix><i class="fas fa-flask"></i></badge-prefix>
          <span translate>Experimental</span>
        </badge>
      </a>
      <div *ngbNavContent class="content-box">
        <mcp-management [user]="user" />
      </div>
    </li>
  }
</ul>
<div [ngbNavOutlet]="nav"></div>
